import { Dispatch, useReducer } from 'react'

import { RfStatusTone } from './components/RfStatusBanner'
import { locationContext } from './locationContext'
import { createReceiptProgress, ReceiptAction, ReceiptProgress, receiptProgress, reduceReceipt } from './receipt'
import {
  LabelPrintRequest,
  LabelType,
  PlannedLabel,
  labelTypeFor,
  planPalletContents,
  receivingPrinters,
} from './labels'
import {
  createMeasurements,
  MeasurementAction,
  MeasurementState,
  measuredProfile,
  reduceMeasurement,
} from './measurements'
import {
  defaultReceivingFixture,
  ReceivingAsnSummary,
  ReceivingFlowFixture,
  ReceivingLocationSummary,
  ReceivingPreprocessProfile,
  ReceivingVariantSummary,
} from './fixtures'

export type ReceivingStep =
  | 'scan-shipment-reference'
  | 'scan-product-to-narrow'
  | 'select-asn'
  | 'confirm-asn'
  | 'scan-line-product'
  | 'enter-line-sku'
  | 'variant-menu'
  | 'over-receive'
  | 'measure-pack'
  | 'confirm-freight-palletization'
  | 'review-label-plan'
  | 'label-processing'
  | 'receiving-location'
  | 'scan-pallet-lpn'
  | 'build-pallet'
  | 'receive-loose-eaches'
  | 'capture-receipt-attributes'
  | 'receipt-attribute-conflict'
  | 'replace-container-label'
  | 'variant-complete'
  | 'stage-asn'
  | 'shortage-review'
  | 'receipt-complete'
  | 'reprint-labels'

export type ReceivingPackLevel = 'inner' | 'carton' | 'pallet'
export type ReceivingPackResolution = 'confirmed' | 'absent' | null

export interface ReceivingLabelPlanItem {
  level: 'Full pallet' | 'Remainder pallet' | 'Receiving pallet' | 'Partial receiving pallet' | 'Carton' | 'Inner pack'
  count: number
  unitsPerContainer: number
}

export interface ReceivingLabelPlan {
  palletized: boolean
  remainingUnits: number
  items: ReceivingLabelPlanItem[]
  remainderEaches: number
  totalLabels: number
}

export interface ReceivingPreprocessState {
  eachConfirmed: boolean
  inner: ReceivingPackResolution
  carton: ReceivingPackResolution
  pallet: ReceivingPackResolution
  evidence: Partial<Record<ReceivingPackLevel, string>>
  freightPalletized: boolean | null
  labelPlan: ReceivingLabelPlan | null
  reservedLpns: string[]
  // Label identities have been prepared. Packaging readiness is derived separately.
  complete: boolean
}

export interface ReceivingFlowStatus {
  tone: RfStatusTone
  message: string
}

export interface ReceivingFlowState {
  shortageReason: string
  shortageDialogOpen: boolean
  captureResult: CaptureResult | null
  asnCompletionDialogOpen: boolean
  pausedTasks: Partial<Record<VariantTask, PausedVariantTask>>
  receipt: ReceiptProgress
  lineProgress: Record<string, ReceivingLineProgress>
  reprintSelection: string
  reprintReason: string
  reprintSubmitted: boolean
  step: ReceivingStep
  reference: string
  productBarcode: string
  asnEntry: string
  candidateAsnIds: string[]
  selectedAsn: ReceivingAsnSummary | null
  lineProductBarcode: string
  lineSku: string
  activeVariant: ReceivingVariantSummary | null
  locationEntry: string
  receivingLocation: ReceivingLocationSummary | null
  additionalByLine: Record<string, number>
  additionalEntry: string
  labels: PlannedLabel[]
  printRequests: LabelPrintRequest[]
  printerId: string
  nextPrintOutcome: 'accepted' | 'rejected'
  printConfirmation: LabelType | null
  preprocess: ReceivingPreprocessState
  measurements: MeasurementState | null
  reservedLpnHistory: string[]
  lineAddedToAsn: boolean
  exceptionEvidence: string[]
  status: ReceivingFlowStatus | null
  exceptionDialogOpen: boolean
  createVariantDialogOpen: boolean
  sectionComplete: boolean
  fixture: ReceivingFlowFixture
}

export interface CaptureResult {
  kind: 'complete' | 'shortage'
  asnId: string
  reason: string
  lines: { sku: string; expected: number; additional: number; target: number; received: number; missing: number }[]
  partialPallets: { lpn: string; planned: number; actual: number }[]
  unusedLpns: string[]
}

export interface ReceivingLineProgress {
  variant: ReceivingVariantSummary
  added: boolean
  preprocess: ReceivingPreprocessState
  measurements: MeasurementState | null
  labels: PlannedLabel[]
  receipt: ReceiptProgress
  pausedTasks: Partial<Record<VariantTask, PausedVariantTask>>
  additionalEntry: string
  reprintSelection: string
  reprintReason: string
}

export type VariantTask = 'preprocess' | 'labels' | 'over' | 'receiving' | 'reprint' | 'stage'
export interface PausedVariantTask {
  step: ReceivingStep
  status: ReceivingFlowStatus | null
}

const taskForStep: Partial<Record<ReceivingStep, VariantTask>> = {
  'measure-pack': 'preprocess',
  'confirm-freight-palletization': 'labels',
  'review-label-plan': 'labels',
  'label-processing': 'labels',
  'over-receive': 'over',
  'receiving-location': 'receiving',
  'scan-pallet-lpn': 'receiving',
  'build-pallet': 'receiving',
  'receive-loose-eaches': 'receiving',
  'capture-receipt-attributes': 'receiving',
  'replace-container-label': 'receiving',
  'variant-complete': 'receiving',
  'reprint-labels': 'reprint',
  'stage-asn': 'stage',
  'shortage-review': 'stage',
}
export const hasUnresolvedOperation = (state: ReceivingFlowState) =>
  !!(
    state.printConfirmation ||
    state.asnCompletionDialogOpen ||
    state.shortageDialogOpen ||
    state.measurements?.dialog ||
    state.reprintSubmitted ||
    receiptProgress(state).replacementSubmitted ||
    state.step === 'receipt-attribute-conflict' ||
    state.exceptionDialogOpen ||
    state.createVariantDialogOpen
  )
export const canReturnToVariantMenu = (state: ReceivingFlowState) =>
  !!state.activeVariant && !!state.selectedAsn && !!taskForStep[state.step] && !hasUnresolvedOperation(state)

export type ReceivingFlowSeed = Partial<Omit<ReceivingFlowState, 'fixture'>> & {
  fixture?: ReceivingFlowFixture
}

export type ReceivingFlowAction =
  | MeasurementAction
  | ReceiptAction
  | { type: 'open-stage-asn' }
  | { type: 'confirm-stage-asn' }
  | { type: 'request-stage-completion' }
  | { type: 'cancel-stage-completion' }
  | { type: 'open-shortage-review' }
  | { type: 'set-shortage-reason'; value: string }
  | { type: 'request-shortage-completion' }
  | { type: 'cancel-shortage-completion' }
  | { type: 'confirm-shortage-completion' }
  | { type: 'open-reprint-labels' }
  | { type: 'set-reprint-selection'; value: string }
  | { type: 'set-reprint-reason'; value: string }
  | { type: 'submit-reprint' }
  | { type: 'confirm-reprint' }
  | { type: 'decline-reprint' }
  | { type: 'set-reference'; reference: string }
  | { type: 'submit-reference' }
  | { type: 'set-product-barcode'; barcode: string }
  | { type: 'submit-product-barcode' }
  | { type: 'set-asn-entry'; asnNumber: string }
  | { type: 'select-asn'; asnId: string }
  | { type: 'submit-asn-selection' }
  | { type: 'open-exception-dialog' }
  | { type: 'close-exception-dialog' }
  | { type: 'confirm-exception-asn' }
  | { type: 'confirm-asn' }
  | { type: 'set-line-product-barcode'; barcode: string }
  | { type: 'submit-line-product-barcode' }
  | { type: 'set-line-sku'; sku: string }
  | { type: 'submit-line-sku' }
  | { type: 'close-create-variant-dialog' }
  | { type: 'confirm-create-variant' }
  | { type: 'start-preprocess' }
  | { type: 'open-over-receive' }
  | { type: 'set-additional-quantity'; value: string }
  | { type: 'submit-additional-quantity' }
  | { type: 'set-freight-palletized'; palletized: boolean }
  | { type: 'reserve-label-identities' }
  | { type: 'open-label-processing' }
  | { type: 'set-label-printer'; printerId: string }
  | { type: 'set-print-outcome'; outcome: 'accepted' | 'rejected' }
  | { type: 'submit-label-print'; labelType: LabelType }
  | { type: 'continue-to-receiving' }
  | { type: 'set-receiving-location'; value: string }
  | { type: 'submit-receiving-location' }
  | { type: 'confirm-labels-printed' }
  | { type: 'decline-labels-printed' }
  | { type: 'select-another-variant' }
  | { type: 'return-variant-menu' }
  | { type: 'back' }
  | { type: 'reset'; seed?: ReceivingFlowSeed }

export interface ReceivingFlowController {
  state: ReceivingFlowState
  dispatch: Dispatch<ReceivingFlowAction>
}

export const receivingContext = (state: ReceivingFlowState) =>
  `ASN: ${state.selectedAsn?.asnNumber ?? '—'}${state.activeVariant ? ` | SKU: ${state.activeVariant.sku}` : ''}`

export const readyForReceiving = (state: ReceivingFlowState): boolean =>
  !state.captureResult &&
  !!state.activeVariant &&
  !!state.selectedAsn &&
  state.preprocess.complete &&
  !!state.labels?.length &&
  state.labels.every((label) => label.printed && label.confirmed)

export const preprocessingComplete = (state: ReceivingFlowState): boolean =>
  !!state.measurements && Object.values(state.measurements.packs).every((pack) => pack.complete)

const normalize = (value?: string) => value?.trim().toUpperCase() ?? ''

const getAsn = (fixture: ReceivingFlowFixture, asnId: string) => fixture.asns.find((asn) => asn.id === asnId) ?? null

const getVariant = (fixture: ReceivingFlowFixture, variantId: string) =>
  (fixture.variants ?? defaultReceivingFixture.variants).find((variant) => variant.id === variantId) ?? null

const addEvidence = (evidence: string[], item: string) => (evidence.includes(item) ? evidence : [...evidence, item])

const hasLabelPrinter = (state: ReceivingFlowState) =>
  receivingPrinters.some((printer) => printer.id === state.printerId)
const hasIssuedLabels = (state: ReceivingFlowState) =>
  state.labels.some(
    (label) =>
      label.printed ||
      label.confirmed ||
      state.printRequests.some((request) => request.status === 'accepted' && request.lpns.includes(label.lpn))
  )
const enterLabelProcessing = (state: ReceivingFlowState): ReceivingFlowState =>
  hasLabelPrinter(state)
    ? {
        ...state,
        step: 'label-processing',
        printConfirmation: state.labels?.find((label) => label.printed && !label.confirmed)?.type ?? null,
      }
    : {
        ...state,
        step: 'variant-menu',
        printConfirmation: null,
        status: {
          tone: 'error',
          message: state.printerId
            ? 'The configured label printer is unavailable. Check RF settings before printing.'
            : 'No label printer configured. Configure a label printer in RF settings before printing.',
        },
      }

const resumeVariantTask = (state: ReceivingFlowState, task: VariantTask): ReceivingFlowState | null => {
  const paused = state.pausedTasks?.[task]
  if (!paused) return null
  if (hasUnresolvedOperation(state)) return state
  if (task === 'receiving' && !readyForReceiving(state))
    return {
      ...state,
      status: { tone: 'error', message: 'Complete preprocessing and labels before resuming receiving.' },
    }
  if (task === 'over' && !canOverReceive(state)) return state
  if (task === 'labels' && !preprocessingComplete(state)) return state
  if (
    task === 'receiving' &&
    !['receiving-location', 'variant-complete'].includes(paused.step) &&
    !state.receivingLocation
  ) {
    return { ...state, step: 'receiving-location', status: null }
  }
  const pausedTasks = { ...state.pausedTasks }
  delete pausedTasks[task]
  // Validation feedback is transient: task data may have changed while visiting another task.
  const step =
    paused.step === 'shortage-review' && !shortagePlan(state).lines.some((line) => line.missing > 0)
      ? 'stage-asn'
      : paused.step
  const next = { ...state, step, status: null, pausedTasks }
  return task === 'labels' && next.step === 'label-processing' ? enterLabelProcessing(next) : next
}

const withoutPausedTasks = (state: ReceivingFlowState, ...tasks: VariantTask[]) => {
  const paused = { ...state.pausedTasks }
  for (const task of tasks) delete paused[task]
  return paused
}

const lineKey = (state: ReceivingFlowState) => `${state.selectedAsn?.id}:${state.activeVariant?.sku}`
export const getReceiptQuantities = (state: ReceivingFlowState) => {
  const expected = state.activeVariant?.expectedUnits ?? 0
  const additional = state.additionalByLine?.[lineKey(state)] ?? 0
  const received = state.activeVariant?.receivedUnits ?? 0
  return {
    expected,
    additional,
    received,
    target: expected + additional,
    remaining: Math.max(expected + additional - received, 0),
  }
}

export const canOverReceive = (state: ReceivingFlowState): boolean =>
  !!state.activeVariant &&
  getReceiptQuantities(state).remaining === 0 &&
  !receiptProgress(state).pending &&
  !Object.values(receiptProgress(state).containers).some(
    (container) => !container.parentLpn && container.status === 'in-progress' && container.received > 0
  ) &&
  (state.lineAddedToAsn ||
    (!!state.measurements && Object.values(state.measurements.packs).every((pack) => pack.complete)))

export const createPreprocessState = (): ReceivingPreprocessState => ({
  eachConfirmed: false,
  inner: null,
  carton: null,
  pallet: null,
  evidence: {},
  freightPalletized: null,
  labelPlan: null,
  reservedLpns: [],
  complete: false,
})

const getPreprocessState = (state: ReceivingFlowState): ReceivingPreprocessState => ({
  ...createPreprocessState(),
  ...(state.preprocess ?? {}),
  evidence: { ...(state.preprocess?.evidence ?? {}) },
  reservedLpns: state.preprocess?.reservedLpns ?? [],
})

export const getActivePreprocessProfile = (state: ReceivingFlowState): ReceivingPreprocessProfile =>
  (state.activeVariant && state.fixture.preprocessProfiles?.[state.activeVariant.id]) ??
  state.fixture.defaultPreprocessProfile ??
  defaultReceivingFixture.defaultPreprocessProfile

const decomposeUnits = (
  units: number,
  state: ReceivingFlowState,
  profile: ReceivingPreprocessProfile,
  items: ReceivingLabelPlanItem[]
) => {
  let remainder = units
  if (state.preprocess.carton === 'confirmed' && remainder >= profile.carton.units) {
    const count = Math.floor(remainder / profile.carton.units)
    items.push({ level: 'Carton', count, unitsPerContainer: profile.carton.units })
    remainder %= profile.carton.units
  }
  if (state.preprocess.inner === 'confirmed' && remainder >= profile.inner.units) {
    const count = Math.floor(remainder / profile.inner.units)
    items.push({ level: 'Inner pack', count, unitsPerContainer: profile.inner.units })
    remainder %= profile.inner.units
  }
  return remainder
}

const buildLabelPlan = (state: ReceivingFlowState, palletized: boolean): ReceivingLabelPlan => {
  const profile = state.measurements ? measuredProfile(state.measurements) : getActivePreprocessProfile(state)
  const remainingUnits = getReceiptQuantities(state).remaining
  const items: ReceivingLabelPlanItem[] = []
  let remainder = remainingUnits

  if (palletized && state.preprocess.pallet === 'confirmed' && remainder >= profile.pallet.units) {
    const count = Math.floor(remainder / profile.pallet.units)
    items.push({ level: 'Full pallet', count, unitsPerContainer: profile.pallet.units })
    remainder %= profile.pallet.units
  }

  if (palletized && remainder > 0) {
    items.push({ level: 'Remainder pallet', count: 1, unitsPerContainer: remainder })
  }

  if (!palletized) {
    const full = Math.floor(remainingUnits / profile.pallet.units)
    const partial = remainingUnits % profile.pallet.units
    if (full) items.push({ level: 'Receiving pallet', count: full, unitsPerContainer: profile.pallet.units })
    if (partial) items.push({ level: 'Partial receiving pallet', count: 1, unitsPerContainer: partial })
  }

  const remainderEaches = decomposeUnits(remainder, state, profile, items)
  const totalLabels = items.reduce((total, item) => total + item.count, 0)
  return { palletized, remainingUnits, items, remainderEaches, totalLabels }
}

const activateVariant = (state: ReceivingFlowState, variant: ReceivingVariantSummary): ReceivingFlowState => {
  const lineAddedToAsn = !state.selectedAsn || !variant.asnIds.includes(state.selectedAsn.id)
  const activeVariant = lineAddedToAsn ? { ...variant, expectedUnits: 0, receivedUnits: 0 } : variant
  const saved = state.lineProgress?.[`${state.selectedAsn?.id}:${variant.sku}`]
  if (saved)
    return {
      ...state,
      step: 'variant-menu',
      activeVariant: saved.variant,
      lineAddedToAsn: saved.added,
      preprocess: saved.preprocess,
      measurements: saved.measurements,
      labels: saved.labels,
      receipt: saved.receipt,
      pausedTasks: saved.pausedTasks ?? {},
      additionalEntry: saved.additionalEntry ?? '',
      reprintSelection: saved.reprintSelection ?? 'all',
      reprintReason: saved.reprintReason ?? '',
      locationEntry: '',
      receivingLocation: null,
      lineSku: saved.variant.sku,
      printConfirmation: null,
      status: null,
    }
  const exceptionEvidence = state.exceptionEvidence ?? []
  return {
    ...state,
    step:
      lineAddedToAsn && getReceiptQuantities({ ...state, activeVariant }).remaining === 0
        ? 'over-receive'
        : 'variant-menu',
    additionalEntry: '',
    activeVariant,
    locationEntry: '',
    receivingLocation: null,
    labels: [],
    printConfirmation: null,
    lineSku: activeVariant.sku,
    lineAddedToAsn,
    preprocess: createPreprocessState(),
    measurements: null,
    receipt: createReceiptProgress(),
    pausedTasks: {},
    reprintSelection: 'all',
    reprintReason: '',
    reprintSubmitted: false,
    exceptionEvidence: lineAddedToAsn ? addEvidence(exceptionEvidence, 'Missing line item') : exceptionEvidence,
    status: lineAddedToAsn
      ? {
          tone: 'warning',
          message: `${activeVariant.sku} was added to this receipt as a non-compliant ASN line. Missing-line evidence was recorded.`,
        }
      : {
          tone: 'success',
          message: `${activeVariant.sku} matched the selected ASN.`,
        },
  }
}

export const getCandidateAsns = (state: ReceivingFlowState) =>
  state.candidateAsnIds
    .map((asnId) => getAsn(state.fixture, asnId))
    .filter((asn): asn is ReceivingAsnSummary => asn !== null)

export const createReceivingFlowState = (seed: ReceivingFlowSeed = {}): ReceivingFlowState => ({
  shortageReason: '',
  shortageDialogOpen: false,
  captureResult: null,
  asnCompletionDialogOpen: false,
  pausedTasks: {},
  receipt: createReceiptProgress(),
  lineProgress: {},
  reprintSelection: 'all',
  reprintReason: '',
  reprintSubmitted: false,
  step: 'scan-shipment-reference',
  reference: '',
  productBarcode: '',
  asnEntry: '',
  candidateAsnIds: [],
  selectedAsn: null,
  lineProductBarcode: '',
  lineSku: '',
  activeVariant: null,
  locationEntry: '',
  receivingLocation: null,
  additionalByLine: {},
  additionalEntry: '',
  labels: [],
  printRequests: [],
  // Represents an existing RF setting in this fixture-only slice, not a print-time fallback.
  printerId: 'receiving-01',
  nextPrintOutcome: 'accepted',
  printConfirmation: null,
  preprocess: createPreprocessState(),
  measurements: null,
  reservedLpnHistory: [],
  lineAddedToAsn: false,
  exceptionEvidence: [],
  status: null,
  exceptionDialogOpen: false,
  createVariantDialogOpen: false,
  sectionComplete: false,
  fixture: defaultReceivingFixture,
  ...seed,
})

const reduceFlow = (state: ReceivingFlowState, action: ReceivingFlowAction): ReceivingFlowState => {
  if (state.captureResult && action.type !== 'reset') return state
  const taskActions: Partial<Record<ReceivingFlowAction['type'], VariantTask>> = {
    'start-preprocess': 'preprocess',
    'open-label-processing': 'labels',
    'open-over-receive': 'over',
    'continue-to-receiving': 'receiving',
    'open-reprint-labels': 'reprint',
    'open-stage-asn': 'stage',
  }
  const task = taskActions[action.type]
  if (task && hasUnresolvedOperation(state)) return state
  if (state.step === 'variant-menu' && task) {
    const resumed = resumeVariantTask(state, task)
    if (resumed) return resumed
  }
  switch (action.type) {
    case 'open-shortage-review':
      return state.step === 'stage-asn' && shortagePlan(state).lines.some((line) => line.missing > 0)
        ? { ...state, step: 'shortage-review', shortageDialogOpen: false, status: null }
        : state
    case 'set-shortage-reason':
      return { ...state, shortageReason: action.value, status: null }
    case 'cancel-shortage-completion':
      return { ...state, shortageDialogOpen: false }
    case 'request-shortage-completion':
    case 'confirm-shortage-completion': {
      if (state.step !== 'shortage-review' || state.captureResult) return state
      const result = shortagePlan(state)
      const reason = (state.shortageReason ?? '').trim()
      const error =
        state.fixture.shortageCompletionAllowed === false
          ? 'Shortage completion is not authorized for this receiver.'
          : !reason
            ? 'Enter a reason for the unreceived quantity.'
            : !result.lines.some((line) => line.missing > 0)
              ? 'There is no remaining quantity to close as a shortage.'
              : scopedLines(state).some(
                    (line) =>
                      line.receipt.pending ||
                      line.receipt.replacementSubmitted ||
                      line.labels.some((label) => label.printed && !label.confirmed)
                  )
                ? 'Resolve pending receipts and print confirmations before finishing with a shortage.'
                : null
      if (error) return { ...state, shortageDialogOpen: false, status: { tone: 'error', message: error } }
      if (action.type === 'request-shortage-completion') return { ...state, shortageDialogOpen: true, status: null }
      if (!state.shortageDialogOpen) return state
      const unused = new Set(result.unusedLpns)
      const partial = new Map(result.partialPallets.map((pallet) => [pallet.lpn, pallet.actual]))
      const reconcile = (line: ReceivingLineProgress): ReceivingLineProgress => ({
        ...line,
        labels: line.labels.map((label) =>
          unused.has(label.lpn)
            ? { ...label, voided: true }
            : partial.has(label.lpn)
              ? { ...label, actualQuantity: partial.get(label.lpn) }
              : label
        ),
        receipt: {
          ...line.receipt,
          activePallet: null,
          containers: Object.fromEntries(
            Object.entries(line.receipt.containers).map(([lpn, container]) => [
              lpn,
              unused.has(lpn)
                ? { ...container, status: 'voided' as const }
                : partial.has(lpn)
                  ? { ...container, status: 'closed-short' as const }
                  : container,
            ])
          ),
        },
        pausedTasks: {},
      })
      const lineProgress = { ...state.lineProgress }
      for (const line of scopedLines(state))
        lineProgress[`${state.selectedAsn?.id}:${line.variant.sku}`] = reconcile(line)
      const active = lineProgress[lineKey(state)]
      return {
        ...state,
        step: 'receipt-complete',
        shortageDialogOpen: false,
        status: null,
        captureResult: { ...result, kind: 'shortage', reason },
        lineProgress,
        labels: active?.labels ?? state.labels,
        receipt: active?.receipt ?? state.receipt,
        pausedTasks: {},
        exceptionEvidence: addEvidence(state.exceptionEvidence, 'Short receipt'),
      }
    }
    case 'return-variant-menu': {
      if (!canReturnToVariantMenu(state)) return state
      const task = taskForStep[state.step]!
      return {
        ...state,
        step: 'variant-menu',
        status: null,
        pausedTasks: {
          ...state.pausedTasks,
          [task]:
            state.step === 'receiving-location' && state.pausedTasks.receiving
              ? state.pausedTasks.receiving
              : { step: state.step, status: state.status },
        },
      }
    }
    case 'set-reference':
      return {
        ...state,
        reference: action.reference,
        status: state.status?.tone === 'error' ? null : state.status,
      }
    case 'submit-reference': {
      const reference = normalize(state.reference)
      state = { ...state, productBarcode: '', asnEntry: '' }
      if (!reference) {
        return {
          ...state,
          step: 'scan-product-to-narrow',
          reference: '',
          candidateAsnIds: [],
          selectedAsn: null,
          status: {
            tone: 'warning',
            message: 'No shipment reference was provided. Scan a product to find an ASN.',
          },
        }
      }

      const candidateAsnIds = state.fixture.referenceMatches[reference] ?? []
      if (candidateAsnIds.length === 1) {
        return {
          ...state,
          step: 'confirm-asn',
          reference,
          candidateAsnIds,
          selectedAsn: getAsn(state.fixture, candidateAsnIds[0]),
          status: null,
        }
      }

      return {
        ...state,
        step: 'scan-product-to-narrow',
        reference,
        candidateAsnIds,
        selectedAsn: null,
        status: {
          tone: candidateAsnIds.length > 1 ? 'info' : 'warning',
          message:
            candidateAsnIds.length > 1
              ? `${candidateAsnIds.length} ASNs match this shipment reference. Scan a product to narrow the results.`
              : 'No ASN matched this shipment reference. Scan a product to search active ASNs.',
        },
      }
    }
    case 'set-product-barcode':
      return {
        ...state,
        productBarcode: action.barcode,
        status: state.status?.tone === 'error' ? null : state.status,
      }
    case 'submit-product-barcode': {
      const productBarcode = normalize(state.productBarcode)
      state = { ...state, asnEntry: '' }
      if (!productBarcode) {
        return {
          ...state,
          step: 'select-asn',
          productBarcode: '',
          candidateAsnIds: [],
          selectedAsn: null,
          status: {
            tone: 'warning',
            message: 'No product barcode was provided. Enter an ASN number or create an ASN.',
          },
        }
      }

      const productAsnIds = state.fixture.productMatches[productBarcode] ?? []
      const referenceAsnIds = state.fixture.referenceMatches[normalize(state.reference)] ?? []
      const candidateAsnIds =
        referenceAsnIds.length > 0 ? productAsnIds.filter((asnId) => referenceAsnIds.includes(asnId)) : productAsnIds

      if (candidateAsnIds.length === 1) {
        return {
          ...state,
          step: 'confirm-asn',
          productBarcode,
          candidateAsnIds,
          selectedAsn: getAsn(state.fixture, candidateAsnIds[0]),
          status: null,
        }
      }

      return {
        ...state,
        step: 'select-asn',
        productBarcode,
        candidateAsnIds,
        selectedAsn: null,
        status: {
          tone: candidateAsnIds.length > 1 ? 'info' : 'warning',
          message:
            candidateAsnIds.length > 1
              ? `${candidateAsnIds.length} candidate ASNs remain. Select one or enter its ASN number.`
              : 'No existing ASN matched both scans. Enter an ASN number or create an ASN.',
        },
      }
    }
    case 'set-asn-entry':
      return {
        ...state,
        asnEntry: action.asnNumber,
        selectedAsn: null,
        status: state.status?.tone === 'error' ? null : state.status,
      }
    case 'select-asn': {
      const selectedAsn = getAsn(state.fixture, action.asnId)
      return {
        ...state,
        selectedAsn,
        asnEntry: selectedAsn?.asnNumber ?? '',
        status: null,
      }
    }
    case 'submit-asn-selection': {
      const enteredAsn = normalize(state.asnEntry)
      const selectedAsn =
        state.selectedAsn ?? state.fixture.asns.find((asn) => normalize(asn.asnNumber) === enteredAsn) ?? null

      if (!selectedAsn) {
        return {
          ...state,
          status: { tone: 'error', message: 'Select a candidate or enter a valid ASN number to continue.' },
        }
      }

      return {
        ...state,
        step: 'confirm-asn',
        selectedAsn,
        asnEntry: selectedAsn.asnNumber,
        status: null,
      }
    }
    case 'open-exception-dialog':
      return { ...state, exceptionDialogOpen: true }
    case 'close-exception-dialog':
      return { ...state, exceptionDialogOpen: false }
    case 'confirm-exception-asn': {
      if (state.step !== 'select-asn' || !state.exceptionDialogOpen) return state
      const selectedAsn: ReceivingAsnSummary = {
        id: 'exception-asn',
        asnNumber: 'Exception ASN',
        company: 'Unassigned',
        sender: 'Unknown sender',
        expectedDate: 'Not scheduled',
        lineCount: 0,
        expectedUnits: 0,
        shipmentReference: state.reference || 'Not provided',
        exception: true,
      }
      return {
        ...state,
        step: 'scan-line-product',
        selectedAsn,
        asnEntry: selectedAsn.asnNumber,
        exceptionDialogOpen: false,
        sectionComplete: true,
        exceptionEvidence: addEvidence(state.exceptionEvidence ?? [], 'No ASN'),
        status: {
          tone: 'warning',
          message: 'Exception ASN created. No-ASN evidence was recorded for receipt review.',
        },
      }
    }
    case 'confirm-asn':
      if (!state.selectedAsn) return state
      return {
        ...state,
        step: 'scan-line-product',
        sectionComplete: true,
        status: {
          tone: 'success',
          message: `${state.selectedAsn.asnNumber} confirmed. Scan a product to select an ASN line.`,
        },
      }
    case 'set-line-product-barcode':
      return { ...state, lineProductBarcode: action.barcode, status: null }
    case 'submit-line-product-barcode': {
      const barcode = normalize(state.lineProductBarcode)
      if (!barcode) {
        return {
          ...state,
          step: 'enter-line-sku',
          lineProductBarcode: '',
          activeVariant: null,
          status: null,
        }
      }

      const variantId = (state.fixture.variantBarcodeMatches ?? defaultReceivingFixture.variantBarcodeMatches)[barcode]
      const variant = variantId ? getVariant(state.fixture, variantId) : null
      if (variant) return activateVariant({ ...state, lineProductBarcode: barcode }, variant)

      return {
        ...state,
        step: 'enter-line-sku',
        lineProductBarcode: barcode,
        activeVariant: null,
        status: {
          tone: 'warning',
          message: 'No product matched this barcode. Enter the product SKU to continue.',
        },
      }
    }
    case 'set-line-sku':
      return { ...state, lineSku: action.sku, status: null }
    case 'submit-line-sku': {
      const sku = normalize(state.lineSku)
      if (!sku) {
        return { ...state, status: { tone: 'error', message: 'Enter a product SKU to continue.' } }
      }

      const variantId = (state.fixture.variantSkuMatches ?? defaultReceivingFixture.variantSkuMatches)[sku]
      const variant = variantId ? getVariant(state.fixture, variantId) : null
      if (variant) return activateVariant({ ...state, lineSku: sku }, variant)

      return {
        ...state,
        lineSku: sku,
        createVariantDialogOpen: true,
        status: null,
      }
    }
    case 'close-create-variant-dialog':
      return {
        ...state,
        step: 'scan-line-product',
        createVariantDialogOpen: false,
        status: null,
      }
    case 'confirm-create-variant': {
      if (state.step !== 'enter-line-sku' || !state.createVariantDialogOpen) return state
      const sku = normalize(state.lineSku)
      const variant: ReceivingVariantSummary = {
        id: `exception-${sku}`,
        sku,
        barcode: normalize(state.lineProductBarcode),
        name: 'New receiving variant',
        description: 'Created during non-compliant receiving',
        asnIds: state.selectedAsn ? [state.selectedAsn.id] : [],
        expectedUnits: 0,
        receivedUnits: 0,
      }
      return {
        ...state,
        step: 'over-receive',
        additionalEntry: '',
        activeVariant: variant,
        receipt: createReceiptProgress(),
        pausedTasks: {},
        fixture: {
          ...state.fixture,
          variants: [...(state.fixture.variants ?? []), variant],
          variantSkuMatches: { ...state.fixture.variantSkuMatches, [sku]: variant.id },
          variantBarcodeMatches: variant.barcode
            ? { ...state.fixture.variantBarcodeMatches, [variant.barcode]: variant.id }
            : state.fixture.variantBarcodeMatches,
        },
        locationEntry: '',
        receivingLocation: null,
        labels: [],
        printConfirmation: null,
        preprocess: createPreprocessState(),
        measurements: null,
        lineSku: sku,
        lineAddedToAsn: true,
        createVariantDialogOpen: false,
        exceptionEvidence: addEvidence(addEvidence(state.exceptionEvidence ?? [], 'Missing SKU'), 'Missing line item'),
        status: {
          tone: 'warning',
          message: `${sku} was created and added as a non-compliant ASN line. Missing-SKU and missing-line evidence were recorded.`,
        },
      }
    }
    case 'open-over-receive':
      return canOverReceive(state) ? { ...state, step: 'over-receive', additionalEntry: '', status: null } : state
    case 'set-additional-quantity':
      return { ...state, additionalEntry: action.value, status: null }
    case 'submit-additional-quantity': {
      if (state.step !== 'over-receive' || !canOverReceive(state)) return state
      const quantity = Number(state.additionalEntry)
      const quantities = getReceiptQuantities(state)
      if (
        !state.additionalEntry.trim() ||
        !Number.isSafeInteger(quantity) ||
        quantity <= 0 ||
        !Number.isSafeInteger(quantities.target + quantity)
      ) {
        return { ...state, status: { tone: 'error', message: 'Enter a positive whole number of additional eaches.' } }
      }
      const next = {
        ...state,
        pausedTasks: withoutPausedTasks(state, 'preprocess', 'receiving', 'labels', 'stage'),
        preprocess: { ...state.preprocess, complete: false, labelPlan: null, reservedLpns: [] },
        additionalByLine: { ...state.additionalByLine, [lineKey(state)]: quantities.additional + quantity },
        additionalEntry: '',
        exceptionEvidence: addEvidence(state.exceptionEvidence, 'Quantity overage'),
        status: null,
      }
      if (preprocessingComplete(next)) {
        return reduceFlow({ ...next, step: 'variant-menu' }, { type: 'open-label-processing' })
      }
      return {
        ...next,
        step: 'measure-pack',
        measurements: next.measurements ?? createMeasurements(),
        preprocess: createPreprocessState(),
      }
    }
    case 'start-preprocess':
      if (!state.activeVariant) return state
      if (hasIssuedLabels(state))
        return {
          ...state,
          status: {
            tone: 'warning',
            message:
              'Labels have been issued. Continue the current plan; packaging changes require label reconciliation.',
          },
        }
      if (receiptProgress(state).pending)
        return {
          ...state,
          status: { tone: 'error', message: 'Resume or cancel the pending receipt before changing preprocessing.' },
        }
      if (receiptProgress(state).events.length)
        return {
          ...state,
          status: {
            tone: 'warning',
            message: 'Receiving has started. Use Over receive to add quantity.',
          },
        }
      if (canOverReceive(state)) return { ...state, step: 'over-receive', additionalEntry: '', status: null }
      return {
        ...state,
        step: 'measure-pack',
        pausedTasks: withoutPausedTasks(state, 'labels', 'receiving', 'stage'),
        measurements: state.measurements ?? createMeasurements(),
        preprocess: createPreprocessState(),
        status: null,
      }
    case 'set-measurement':
    case 'submit-measurement':
    case 'confirm-measurement-update':
    case 'cancel-measurement-dialog':
    case 'skip-pack':
    case 'add-measurement-photos':
    case 'retake-measurement-photos':
    case 'accept-measurement-photos':
    case 'confirm-pack-absence':
    case 'measurement-back': {
      if (state.step !== 'measure-pack' || !state.measurements) return state
      const measurements = reduceMeasurement(
        state.measurements,
        action,
        state.fixture.preprocessProfiles?.[state.activeVariant?.id ?? ''] ?? null
      )
      const complete = Object.values(measurements.packs).every((pack) => pack.complete)
      const resolution = (level: ReceivingPackLevel): ReceivingPackResolution =>
        measurements.packs[level].complete ? (measurements.packs[level].absent ? 'absent' : 'confirmed') : null
      return {
        ...state,
        measurements,
        step: complete ? 'variant-menu' : 'measure-pack',
        status: null,
        preprocess: {
          ...createPreprocessState(),
          eachConfirmed: measurements.packs.each.complete,
          inner: resolution('inner'),
          carton: resolution('carton'),
          pallet: resolution('pallet'),
          evidence: Object.fromEntries(
            Object.entries(measurements.packs).map(([level, pack]) => [level, pack.photos.join(', ')])
          ),
        },
      }
    }
    case 'set-freight-palletized': {
      if (!state.measurements || !Object.values(state.measurements.packs).every((pack) => pack.complete)) return state
      if (getReceiptQuantities(state).remaining <= 0)
        return { ...state, step: 'over-receive', additionalEntry: '', status: null }
      if (
        receiptProgress(state).events.length &&
        state.labels.some(
          (label) => label.type === 'Pallet LPN' && receiptProgress(state).containers[label.lpn]?.status !== 'received'
        )
      ) {
        return {
          ...state,
          status: { tone: 'error', message: 'Finish the current pallet plan before creating another plan.' },
        }
      }
      const preprocess = getPreprocessState(state)
      return {
        ...state,
        step: 'review-label-plan',
        preprocess: {
          ...preprocess,
          freightPalletized: action.palletized,
          labelPlan: buildLabelPlan({ ...state, preprocess }, action.palletized),
          reservedLpns: [],
          complete: false,
        },
        status: null,
      }
    }
    case 'reserve-label-identities': {
      const preprocess = getPreprocessState(state)
      const labelPlan = preprocess.labelPlan
      if (!labelPlan) return state
      if (preprocess.complete) return enterLabelProcessing(state)
      const reservedLpnHistory = state.reservedLpnHistory ?? []
      const availableLpns = (state.fixture.availableLpns ?? defaultReceivingFixture.availableLpns).filter(
        (lpn) => !reservedLpnHistory.includes(lpn)
      )
      if (availableLpns.length < labelPlan.totalLabels) {
        return { ...state, status: { tone: 'error', message: 'Not enough fixture LPN identities are available.' } }
      }
      const reservedLpns = availableLpns.slice(0, labelPlan.totalLabels)
      const profile = state.measurements ? measuredProfile(state.measurements) : getActivePreprocessProfile(state)
      let identityIndex = 0
      const labels = labelPlan.items.flatMap((item) =>
        Array.from({ length: item.count }, () => ({
          lpn: reservedLpns[identityIndex++],
          type: labelTypeFor(item.level),
          level: item.level,
          quantity: item.unitsPerContainer,
          sku: state.activeVariant?.sku ?? '',
          printed: false,
          confirmed: false,
          contents:
            labelTypeFor(item.level) === 'Pallet LPN' && item.level !== 'Full pallet'
              ? planPalletContents(
                  item.unitsPerContainer,
                  preprocess.carton === 'confirmed' ? profile.carton.units : 0,
                  preprocess.inner === 'confirmed' ? profile.inner.units : 0
                )
              : undefined,
        }))
      )
      return enterLabelProcessing({
        ...state,
        step: 'label-processing',
        labels: [
          ...state.labels.filter((label) => receiptProgress(state).containers[label.lpn]?.status === 'received'),
          ...labels,
        ],
        pausedTasks: withoutPausedTasks(state, 'labels', 'receiving', 'stage'),
        printConfirmation: null,
        reservedLpnHistory: [...reservedLpnHistory, ...reservedLpns],
        preprocess: {
          ...preprocess,
          reservedLpns,
          complete: true,
        },
        status: {
          tone: 'success',
          message:
            labelPlan.totalLabels > 0
              ? `${labelPlan.totalLabels} labels prepared. Ready to print.`
              : 'No new labels required.',
        },
      })
    }
    case 'open-label-processing':
      if (!preprocessingComplete(state)) return state
      if (state.preprocess.complete) return enterLabelProcessing({ ...state, status: null })
      if (state.preprocess.labelPlan) return { ...state, step: 'review-label-plan', status: null }
      if (state.preprocess.freightPalletized !== null)
        return reduceFlow(state, { type: 'set-freight-palletized', palletized: state.preprocess.freightPalletized })
      return { ...state, step: 'confirm-freight-palletization', status: null }
    case 'set-label-printer': {
      const next = { ...state, printerId: action.printerId, status: null }
      return state.step === 'label-processing' ? enterLabelProcessing(next) : next
    }
    case 'set-print-outcome':
      return { ...state, nextPrintOutcome: action.outcome }
    case 'submit-label-print': {
      if (state.step !== 'label-processing' || !state.preprocess.complete) return state
      if (state.printConfirmation) return state
      if (!hasLabelPrinter(state)) return enterLabelProcessing(state)
      const pending = (state.labels ?? []).filter((label) => label.type === action.labelType && !label.printed)
      if (!pending.length) return state
      const lpns = pending.map((label) => label.lpn)
      const previous = (state.printRequests ?? []).find(
        (request) =>
          request.status === 'rejected' &&
          request.printerId === state.printerId &&
          request.lpns.length === lpns.length &&
          request.lpns.every((lpn, i) => lpn === lpns[i])
      )
      const status = state.nextPrintOutcome ?? 'accepted'
      const declined = [...(state.printRequests ?? [])]
        .reverse()
        .find((entry) => entry.outputConfirmation === 'declined' && entry.lpns.some((lpn) => lpns.includes(lpn)))
      const request: LabelPrintRequest = {
        id: previous?.id ?? `print-${(state.printRequests?.length ?? 0) + 1}`,
        printerId: state.printerId,
        template: 'receiving-lpn-v1',
        lpns,
        status,
        attempts: (previous?.attempts ?? 0) + 1,
        replacesRequestId: declined?.id,
      }
      return {
        ...state,
        labels: state.labels.map((label) =>
          lpns.includes(label.lpn) && status === 'accepted' ? { ...label, printed: true } : label
        ),
        printRequests: [...(state.printRequests ?? []).filter((entry) => entry.id !== request.id), request],
        nextPrintOutcome: 'accepted',
        printConfirmation: status === 'accepted' ? action.labelType : null,
        status:
          status === 'accepted'
            ? {
                tone: 'success',
                message: `${lpns.length} ${action.labelType} label(s) submitted to ${receivingPrinters.find((printer) => printer.id === state.printerId)?.name}.`,
              }
            : {
                tone: 'error',
                message: 'Print request was not accepted. Check the printer and retry the same labels.',
              },
      }
    }
    case 'decline-labels-printed': {
      if (state.step !== 'label-processing' || !state.printConfirmation) return state
      const lpns = state.labels
        .filter((label) => label.type === state.printConfirmation && label.printed && !label.confirmed)
        .map((label) => label.lpn)
      return {
        ...state,
        printConfirmation: null,
        status: null,
        labels: state.labels.map((label) => (lpns.includes(label.lpn) ? { ...label, printed: false } : label)),
        printRequests: state.printRequests.map((request) =>
          request.status === 'accepted' && !request.outputConfirmation && request.lpns.some((lpn) => lpns.includes(lpn))
            ? { ...request, outputConfirmation: 'declined' }
            : request
        ),
      }
    }
    case 'confirm-labels-printed':
      if (state.step !== 'label-processing' || !state.printConfirmation) return state
      return {
        ...state,
        labels: state.labels.map((label) =>
          label.type === state.printConfirmation && label.printed ? { ...label, confirmed: true } : label
        ),
        printRequests: state.printRequests.map((request) =>
          request.status === 'accepted' &&
          !request.outputConfirmation &&
          request.lpns.some((lpn) =>
            state.labels.some((label) => label.lpn === lpn && label.type === state.printConfirmation)
          )
            ? { ...request, outputConfirmation: 'confirmed' }
            : request
        ),
        printConfirmation: null,
        status: null,
      }
    case 'continue-to-receiving':
      if (!['label-processing', 'variant-menu'].includes(state.step) || !readyForReceiving(state)) return state
      if (state.pausedTasks.receiving) return resumeVariantTask(state, 'receiving') ?? state
      if (
        getReceiptQuantities(state).remaining === 0 &&
        !Object.values(receiptProgress(state).containers).some(
          (container) => !container.parentLpn && container.status === 'in-progress' && container.received > 0
        )
      )
        return { ...state, step: 'variant-complete', status: null }
      return { ...state, step: state.receivingLocation ? 'scan-pallet-lpn' : 'receiving-location', status: null }
    case 'set-receipt-scan':
    case 'scan-receiving-pallet':
    case 'scan-receiving-child':
    case 'open-loose-receipt':
    case 'set-loose-quantity':
    case 'submit-loose-receipt':
    case 'switch-receiving-pallet':
    case 'finish-receiving-pallet':
    case 'done-pallet-work':
    case 'set-controlled-attribute':
    case 'submit-controlled-attributes':
    case 'cancel-pending-receipt':
    case 'print-container-replacement':
    case 'confirm-container-replacement':
    case 'decline-container-replacement':
    case 'receipt-back':
    case 'set-fixture-inventory-category':
      return reduceReceipt(state, action)
    case 'open-stage-asn':
      return state.selectedAsn ? { ...state, step: 'stage-asn', asnCompletionDialogOpen: false, status: null } : state
    case 'request-stage-completion':
    case 'confirm-stage-asn': {
      if (state.step !== 'stage-asn') return state
      const incomplete = scopedLines(state).some(
        (line) =>
          !!line.receipt.pending ||
          line.receipt.replacementSubmitted ||
          line.labels.some((label) => label.printed && !label.confirmed) ||
          Object.values(line.receipt.containers).some(
            (container) => container.status === 'in-progress' && container.received > 0
          )
      )
      if (incomplete)
        return {
          ...state,
          asnCompletionDialogOpen: false,
          status: {
            tone: 'error',
            message: 'Finish in-progress pallets and pending receipts before completing receipt capture.',
          },
        }
      if (receiptSummary(state).some((line) => line.receivedUnits < line.target))
        return {
          ...state,
          asnCompletionDialogOpen: false,
          status: {
            tone: 'error',
            message: 'Planned quantity remains. Continue receiving or review the unreceived quantity.',
          },
        }
      if (action.type === 'request-stage-completion') return { ...state, asnCompletionDialogOpen: true, status: null }
      if (!state.asnCompletionDialogOpen) return state
      return {
        ...state,
        step: 'receipt-complete',
        asnCompletionDialogOpen: false,
        status: null,
        captureResult: { ...shortagePlan(state), kind: 'complete', reason: '' },
      }
    }
    case 'cancel-stage-completion':
      return { ...state, asnCompletionDialogOpen: false }
    case 'open-reprint-labels':
      return state.labels.some((label) => label.printed)
        ? {
            ...state,
            step: 'reprint-labels',
            reprintSelection: 'all',
            reprintReason: '',
            reprintSubmitted: false,
            status: null,
          }
        : state
    case 'set-reprint-selection':
      return { ...state, reprintSelection: action.value }
    case 'set-reprint-reason':
      return { ...state, reprintReason: action.value }
    case 'submit-reprint': {
      if (state.step !== 'reprint-labels' || state.reprintSubmitted) return state
      if (!hasLabelPrinter(state))
        return { ...state, status: { tone: 'error', message: 'No valid label printer configured. Check RF settings.' } }
      if (!state.reprintReason.trim())
        return { ...state, status: { tone: 'error', message: 'Enter a reprint reason.' } }
      const lpns = state.labels
        .filter((label) => label.printed && (state.reprintSelection === 'all' || label.lpn === state.reprintSelection))
        .map((label) => label.lpn)
      if (!lpns.length) return { ...state, status: { tone: 'error', message: 'Select an eligible label.' } }
      const status = state.nextPrintOutcome ?? 'accepted'
      return {
        ...state,
        nextPrintOutcome: 'accepted',
        reprintSubmitted: status === 'accepted',
        printRequests: [
          ...state.printRequests,
          {
            id: `print-${state.printRequests.length + 1}`,
            printerId: state.printerId,
            template: 'receiving-lpn-v1',
            lpns,
            status,
            attempts: 1,
            reason: state.reprintReason.trim(),
          },
        ],
        status:
          status === 'accepted'
            ? null
            : { tone: 'error', message: 'Reprint request was not accepted. Retry the same identities.' },
      }
    }
    case 'decline-reprint':
      return {
        ...state,
        reprintSubmitted: false,
        printRequests: state.printRequests.map((entry, index) =>
          index === state.printRequests.length - 1 && state.reprintSubmitted
            ? { ...entry, outputConfirmation: 'declined' }
            : entry
        ),
      }
    case 'confirm-reprint':
      return state.step === 'reprint-labels' && state.reprintSubmitted
        ? {
            ...state,
            printRequests: state.printRequests.map((entry, index) =>
              index === state.printRequests.length - 1 ? { ...entry, outputConfirmation: 'confirmed' } : entry
            ),
            reprintSubmitted: false,
            step: 'variant-menu',
            status: { tone: 'success', message: 'Labels reprinted with the same identities.' },
          }
        : state
    case 'set-receiving-location':
      return state.step === 'receiving-location' ? { ...state, locationEntry: action.value, status: null } : state
    case 'submit-receiving-location': {
      if (state.step !== 'receiving-location' || !readyForReceiving(state)) return state
      const barcode = normalize(state.locationEntry)
      const location = locationContext(state.fixture).locations.find((entry) => entry.barcode === barcode)
      const message = !barcode
        ? 'Scan or enter a receiving location.'
        : !location
          ? `Location ${barcode} was not found.`
          : location.warehouseId !== locationContext(state.fixture).warehouseId
            ? `Location ${barcode} is not in this warehouse.`
            : !location.active
              ? `Location ${barcode} is inactive.`
              : null
      if (message) return { ...state, status: { tone: 'error', message } }
      const r = receiptProgress(state)
      const parent = r.activePallet ?? r.pending?.parentLpn
      const parentLocation = parent ? r.containers[parent]?.location : undefined
      if (state.pausedTasks?.receiving && parentLocation && parentLocation !== barcode)
        return { ...state, status: { tone: 'error', message: `Resume this pallet at ${parentLocation}.` } }
      const next: ReceivingFlowState = {
        ...state,
        locationEntry: barcode,
        receivingLocation: location!,
        step: 'scan-pallet-lpn',
        status: null,
      }
      return resumeVariantTask(next, 'receiving') ?? next
    }
    case 'select-another-variant':
      if (hasUnresolvedOperation(state)) return state
      return {
        ...state,
        step: 'scan-line-product',
        lineProductBarcode: '',
        lineSku: '',
        activeVariant: null,
        pausedTasks: {},
        labels: [],
        locationEntry: '',
        receivingLocation: null,
        printConfirmation: null,
        preprocess: createPreprocessState(),
        lineAddedToAsn: false,
        status: null,
      }
    case 'back':
      if (state.exceptionDialogOpen) return reduceFlow(state, { type: 'close-exception-dialog' })
      if (state.createVariantDialogOpen) return reduceFlow(state, { type: 'close-create-variant-dialog' })
      if (state.measurements?.dialog) return reduceFlow(state, { type: 'cancel-measurement-dialog' })
      if (state.reprintSubmitted) return reduceFlow(state, { type: 'decline-reprint' })
      if (receiptProgress(state).replacementSubmitted)
        return reduceReceipt(state, { type: 'decline-container-replacement' })
      if (state.shortageDialogOpen) return { ...state, shortageDialogOpen: false }
      if (state.step === 'shortage-review') return { ...state, step: 'stage-asn', status: null }
      if (state.asnCompletionDialogOpen) return { ...state, asnCompletionDialogOpen: false }
      if (state.printConfirmation) return receivingFlowReducer(state, { type: 'decline-labels-printed' })
      if (
        [
          'build-pallet',
          'receive-loose-eaches',
          'capture-receipt-attributes',
          'receipt-attribute-conflict',
          'replace-container-label',
          'variant-complete',
        ].includes(state.step)
      )
        return reduceReceipt(state, { type: 'receipt-back' })
      if (state.step === 'stage-asn' || state.step === 'reprint-labels')
        return {
          ...state,
          step: state.activeVariant ? 'variant-menu' : 'scan-line-product',
          reprintSubmitted: false,
          status: null,
        }
      if (state.step === 'scan-pallet-lpn')
        return {
          ...state,
          step: 'receiving-location',
          locationEntry: state.receivingLocation?.barcode ?? '',
          status: null,
        }
      if (state.step === 'receiving-location')
        return state.pausedTasks.receiving
          ? { ...state, step: 'variant-menu', status: null }
          : enterLabelProcessing({ ...state, status: null })
      if (state.step === 'over-receive') return { ...state, step: 'variant-menu', additionalEntry: '', status: null }
      if (state.step === 'measure-pack' && state.measurements) {
        if (
          state.measurements.level === 'each' &&
          state.measurements.field === 'length' &&
          !state.measurements.photo &&
          !state.measurements.dialog
        ) {
          return { ...state, step: 'variant-menu', status: null }
        }
        return receivingFlowReducer(state, { type: 'measurement-back' })
      }
      if (state.step === 'scan-product-to-narrow') {
        return createReceivingFlowState({
          fixture: state.fixture,
          reference: state.reference,
          printerId: state.printerId,
        })
      }
      if (state.step === 'select-asn') {
        return {
          ...state,
          step: 'scan-product-to-narrow',
          selectedAsn: null,
          asnEntry: '',
          status: null,
        }
      }
      if (state.step === 'confirm-asn') {
        let step: ReceivingStep = 'scan-shipment-reference'
        if (state.asnEntry) step = 'select-asn'
        else if (state.productBarcode) step = 'scan-product-to-narrow'
        return {
          ...state,
          step,
          selectedAsn: null,
          sectionComplete: false,
          status: null,
        }
      }
      if (state.step === 'scan-line-product') {
        if (Object.keys(state.lineProgress).some((key) => key.startsWith(`${state.selectedAsn?.id}:`)))
          return { ...state, step: 'stage-asn', status: null }
        return {
          ...state,
          step: 'confirm-asn',
          sectionComplete: false,
          status: null,
        }
      }
      if (state.step === 'enter-line-sku') {
        return {
          ...state,
          step: 'scan-line-product',
          lineSku: '',
          createVariantDialogOpen: false,
          status: null,
        }
      }
      if (state.step === 'variant-menu') {
        return {
          ...state,
          step: 'scan-line-product',
          activeVariant: null,
          locationEntry: '',
          receivingLocation: null,
          lineAddedToAsn: false,
          status: null,
        }
      }
      if (state.step === 'confirm-freight-palletization') {
        return reduceFlow(state, { type: 'return-variant-menu' })
      }
      if (state.step === 'review-label-plan') {
        if (state.preprocess.complete && hasIssuedLabels(state))
          return reduceFlow(state, { type: 'return-variant-menu' })
        return { ...state, step: 'confirm-freight-palletization', status: null }
      }
      if (state.step === 'label-processing') {
        if (hasIssuedLabels(state)) return { ...state, step: 'variant-menu', status: null }
        return { ...state, step: 'review-label-plan', status: null }
      }
      return state
    case 'reset':
      return createReceivingFlowState({
        fixture: action.seed?.fixture ?? state.fixture,
        printerId: state.printerId,
        ...action.seed,
      })
  }
}

export const receivingFlowReducer = (state: ReceivingFlowState, action: ReceivingFlowAction): ReceivingFlowState => {
  const next = reduceFlow(state, action)
  if (next === state || !next.activeVariant || !next.selectedAsn) return next
  const key = `${next.selectedAsn.id}:${next.activeVariant.sku}`
  return {
    ...next,
    lineProgress: {
      ...next.lineProgress,
      [key]: {
        variant: next.activeVariant,
        added: next.lineAddedToAsn,
        preprocess: next.preprocess,
        measurements: next.measurements,
        labels: next.labels,
        receipt: receiptProgress(next),
        pausedTasks: next.pausedTasks ?? {},
        additionalEntry: next.additionalEntry,
        reprintSelection: next.reprintSelection,
        reprintReason: next.reprintReason,
      },
    },
  }
}

export const receiptSummary = (state: ReceivingFlowState) => {
  const lines = new Map<string, ReceivingVariantSummary>()
  for (const variant of state.fixture.variants ?? [])
    if (variant.asnIds.includes(state.selectedAsn?.id ?? '')) lines.set(variant.sku, variant)
  for (const [key, progress] of Object.entries(state.lineProgress ?? {}))
    if (key.startsWith(`${state.selectedAsn?.id}:`)) lines.set(progress.variant.sku, progress.variant)
  if (state.activeVariant) lines.set(state.activeVariant.sku, state.activeVariant)
  return [...lines.values()].map((variant) => {
    const additional = state.additionalByLine?.[`${state.selectedAsn?.id}:${variant.sku}`] ?? 0
    return {
      ...variant,
      additional,
      target: variant.expectedUnits + additional,
      variance: variant.receivedUnits - variant.expectedUnits,
    }
  })
}

const scopedLines = (state: ReceivingFlowState): ReceivingLineProgress[] => {
  const lines = Object.fromEntries(
    Object.entries(state.lineProgress ?? {}).filter(([key]) => key.startsWith(`${state.selectedAsn?.id}:`))
  )
  if (state.activeVariant && state.selectedAsn)
    lines[lineKey(state)] = {
      variant: state.activeVariant,
      added: state.lineAddedToAsn,
      preprocess: state.preprocess,
      measurements: state.measurements,
      labels: state.labels,
      receipt: receiptProgress(state),
      pausedTasks: state.pausedTasks,
      additionalEntry: state.additionalEntry,
      reprintSelection: state.reprintSelection,
      reprintReason: state.reprintReason,
    }
  return Object.values(lines)
}

export const shortagePlan = (state: ReceivingFlowState): CaptureResult => {
  const partialPallets: CaptureResult['partialPallets'] = []
  const unusedLpns: string[] = []
  for (const line of scopedLines(state)) {
    for (const label of line.labels) {
      const container = line.receipt.containers[label.lpn]
      if (!container?.received) unusedLpns.push(label.lpn)
      else if (label.type === 'Pallet LPN' && container.status === 'in-progress')
        partialPallets.push({ lpn: label.lpn, planned: label.quantity, actual: container.received })
    }
  }
  return {
    kind: 'shortage',
    asnId: state.selectedAsn?.id ?? '',
    reason: state.shortageReason ?? '',
    lines: receiptSummary(state).map((line) => ({
      sku: line.sku,
      expected: line.expectedUnits,
      additional: line.additional,
      target: line.target,
      received: line.receivedUnits,
      missing: Math.max(0, line.target - line.receivedUnits),
    })),
    partialPallets,
    unusedLpns,
  }
}

export const useReceivingFlow = (seed: ReceivingFlowSeed = {}): ReceivingFlowController => {
  const [state, dispatch] = useReducer(receivingFlowReducer, seed, createReceivingFlowState)
  return { state, dispatch }
}
